import { classMerge } from "../utility/classMerge";
import { COL_CLICKED } from "./context/interface";
import { isColInSelection } from "./context/scaffoldSelection";
import { SCAFFOLD_SELECT_HANDLE_SIZE_PX } from "./interface";
import { useScaffold } from "./Scaffold";
import styles from "./Scaffold.module.css";

export interface ScaffoldColSelectHandleProps {
  /** The column ID that this handle represents. Clicking it selects or deselects the column. */
  colId: string;
  /** Horizontal offset in pixels from the left edge of the scaffold table where this handle begins. */
  left: number;
  /** Width in pixels of this handle, matching the column width. */
  width: number;
}

/**
 * A thin selection handle rendered at the top edge of a scaffold column.
 * Clicking it selects the column; ctrl+click toggles; shift+click range-selects.
 * The handle is highlighted when its column is selected.
 *
 * @param props - Component props.
 * @param props.colId - The column ID to select when this handle is clicked.
 * @param props.left - Left offset in pixels for positioning the handle over its column.
 * @param props.width - Width in pixels matching the column width.
 * @returns A button element used as the column selection handle.
 */
export const ScaffoldColSelectHandle = ({ colId, left, width }: ScaffoldColSelectHandleProps) => {
  const scaffoldContext = useScaffold();
  const isSelected = isColInSelection(scaffoldContext.state.selection, colId);

  return (
    <button
      type="button"
      aria-label="Select column"
      className={classMerge([
        styles.colSelectHandle,
        isSelected ? styles.colSelectHandleSelected : undefined,
      ])}
      style={{ left, width, height: SCAFFOLD_SELECT_HANDLE_SIZE_PX }}
      onClick={(event) => {
        event.preventDefault();
        scaffoldContext.dispatch({
          operation: COL_CLICKED,
          colId,
          ctrlKey: event.ctrlKey,
          metaKey: event.metaKey,
          shiftKey: event.shiftKey,
        });
      }}
    />
  );
};

ScaffoldColSelectHandle.displayName = "ScaffoldColSelectHandle";
